import { motion } from "framer-motion";
import { Check, Truck } from "lucide-react";
import { SERVICES, SERVICES_TAGLINE, SERVICES_NOTES } from "@/data/content";
import { useSiteContent } from "@/context/SiteContentContext";
import { Reveal, Overline } from "@/components/site/Reveal";
import { scrollToId } from "@/lib/scroll";

export default function Services() {
  const { servicesImages } = useSiteContent();
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-charcoal py-24 lg:py-36"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <Reveal>
              <Overline className="mb-6">Detailing Packages</Overline>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream uppercase tracking-tight leading-none">
                Choose Your <span className="gold-text italic lowercase">detail.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-cream/55 text-sm sm:text-base font-light leading-relaxed">
              {SERVICES_TAGLINE}
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.1} y={36} className="h-full">
              <motion.div
                data-testid={`service-card-${s.id}`}
                whileHover="hover"
                className={`group relative h-full flex flex-col bg-panel border transition-colors duration-500 overflow-hidden ${
                  s.featured
                    ? "border-gold/60"
                    : "border-white/5 hover:border-gold/40"
                }`}
              >
                {s.featured && (
                  <span className="absolute top-0 right-0 z-20 bg-gold text-ink font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-1.5">
                    Most Popular
                  </span>
                )}

                <div className="relative h-44 overflow-hidden">
                  <motion.img
                    variants={{ hover: { scale: 1.08 } }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    src={servicesImages[s.id] || s.image}
                    alt={s.name}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-panel via-panel/30 to-transparent" />
                  <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] text-gold">
                    0{i + 1}
                  </span>
                </div>

                <div className="p-7 flex flex-col flex-1">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-gold/70 uppercase">
                    {s.tier}
                  </span>
                  <h3 className="mt-2 font-serif text-3xl text-cream leading-tight">
                    {s.name}
                  </h3>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="font-serif text-4xl gold-text">{s.price}</span>
                  </div>
                  <p className="mt-4 text-sm text-cream/55 font-light leading-relaxed">
                    {s.description}
                  </p>

                  <div className="divider-gold my-6" />

                  <ul className="space-y-3 flex-1">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <Check
                          size={15}
                          className="text-gold mt-0.5 shrink-0"
                          strokeWidth={2.5}
                        />
                        <span className="text-sm text-cream/70 font-light leading-snug">
                          {f}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid={`service-book-${s.id}`}
                    onClick={() => scrollToId("contact")}
                    className={`mt-8 w-full inline-flex items-center justify-center px-6 py-3.5 font-mono text-[11px] tracking-[0.22em] uppercase transition-all duration-400 ${
                      s.featured
                        ? "bg-gold text-ink hover:bg-champagne"
                        : "border border-gold/60 text-gold hover:bg-gold hover:text-ink"
                    }`}
                  >
                    Book This Package
                  </button>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>

        {/* Important notes */}
        <Reveal delay={0.1}>
          <div
            data-testid="services-notes"
            className="mt-10 border-l-2 border-gold/30 bg-panel/40 p-6 sm:p-8"
          >
            <span className="font-mono text-[10px] tracking-[0.3em] text-gold uppercase">
              Important Notes
            </span>
            <ul className="mt-4 space-y-2.5">
              {SERVICES_NOTES.map((n) => (
                <li key={n} className="flex items-start gap-3">
                  <span className="text-gold/60 mt-1.5 text-[8px]">◆</span>
                  <span className="text-sm text-cream/50 font-light leading-relaxed">
                    {n}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* Pickup & Drop-off highlight */}
        <Reveal y={36}>
          <div
            data-testid="pickup-dropoff-banner"
            className="group relative mt-8 overflow-hidden border border-gold/40 bg-gradient-to-r from-panel via-graphite to-panel"
          >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,rgba(201,162,39,0.14)_0%,transparent_55%)]" />
            <div className="absolute -right-10 -top-10 opacity-[0.06] pointer-events-none">
              <Truck size={220} strokeWidth={0.6} className="text-gold" />
            </div>

            <div className="relative flex flex-col lg:flex-row lg:items-center gap-8 p-8 sm:p-10 lg:p-12">
              <div className="shrink-0 flex items-center justify-center h-16 w-16 rounded-full border border-gold/50 text-gold">
                <Truck size={28} strokeWidth={1.5} />
              </div>

              <div className="flex-1">
                <span className="font-mono text-[10px] tracking-[0.3em] text-gold uppercase">
                  Optional Add-On
                </span>
                <h3 className="mt-2 font-serif text-3xl sm:text-4xl text-cream leading-tight">
                  Pickup &amp; <span className="gold-text italic">Drop-off</span>
                </h3>
                <p className="mt-3 text-cream/60 font-light leading-relaxed max-w-2xl">
                  Too busy to drop your bike in? We'll come to you. We collect your
                  motorcycle, detail it to MOTO LUXE standard, and return it looking its
                  best — a convenient add-on to any service.
                </p>
                <p className="mt-3 font-mono text-[11px] tracking-[0.15em] text-gold/80 uppercase">
                  Price calculated by distance · Ask when you book
                </p>
              </div>

              <button
                data-testid="pickup-dropoff-enquire-button"
                onClick={() => scrollToId("contact")}
                className="shrink-0 self-start lg:self-center inline-flex items-center justify-center gap-2 border border-gold text-gold hover:bg-gold hover:text-ink transition-all duration-400 px-8 py-4 font-mono text-[11px] tracking-[0.22em] uppercase"
              >
                Enquire Now
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
